import React, { useCallback, useContext, useEffect, useRef } from 'react';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import styled, { css } from 'styled-components';
import { Button, ButtonProps, buttonType } from '../button';
import { mediaQuery, typography } from '../../theme';
import { colors } from '../../theme/color';
import ClearIcon from '@mui/icons-material/Clear';
import { useModalState } from './useModalState';
import { modalStore } from './modalStore';
import { LoadingIconCoupon } from '../loading/LoadingIconCoupon';
import { useMediaQuery } from 'react-responsive';
import { getIsNativePlatform } from '../../useCapacitor';

type Context = {
  name: string;
  openModal: () => void;
  closeModal: () => void;
  isOpen: () => boolean;
};

const initialContext = {
  name: '',
  openModal: () => undefined,
  closeModal: () => undefined,
  isOpen: () => false,
};

export const ModalContext = React.createContext<Context>(initialContext);

type Props = {
  name: string;
  open?: boolean;
  openModal?: () => void;
  closeModal?: () => void;
  onClose?: () => void;
  onOpen?: () => void;
  children: React.ReactNode;
};

export const Modal: React.FC<Props> = ({
  name,
  children,
  openModal: controlledOpenModal,
  closeModal: controlledCloseModal,
  open: controlledOpen,
  onClose,
  onOpen,
}) => {
  const { closeModal, openModal, isOpen } = useModalState();
  const wasOpen = useRef<boolean>(false);

  const closeModalWrapper = useCallback(() => {
    if (controlledCloseModal !== undefined) {
      controlledCloseModal();
    } else {
      closeModal(name);
    }
  }, [closeModal, controlledCloseModal, name]);
  const openModalWrapper = useCallback(() => {
    if (controlledOpenModal !== undefined) {
      controlledOpenModal();
    } else {
      openModal(name);
    }
  }, [openModal, controlledOpenModal, name]);
  const isOpenModalWrapper = useCallback(() => {
    if (controlledOpen !== undefined) {
      return controlledOpen;
    } else {
      return isOpen(name);
    }
  }, [isOpen, controlledOpen, name]);

  const open = controlledOpen !== undefined ? controlledOpen : isOpen(name);

  useEffect(() => {
    return () => {
      if (controlledOpen === undefined && controlledCloseModal === undefined) {
        modalStore.closeModal(name);
      }
    };
  }, [name, controlledOpen, controlledCloseModal]);

  useEffect(() => {
    if (open === true && onOpen) {
      onOpen();
    }
    if (open === false && wasOpen.current && onClose) {
      onClose();
    }
    if (open) {
      wasOpen.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onClose and onOpen aren't always callbacked and don't need to run this effect
  }, [open]);
  return (
    <ModalContext.Provider
      value={{
        name,
        closeModal: closeModalWrapper,
        openModal: openModalWrapper,
        isOpen: isOpenModalWrapper,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export const ModalDismissButton: React.FC<{
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  className?: string;
  disabled?: boolean;
  noWrapper?: boolean;
  children?: React.ReactNode;
}> = ({ onClick, className, children, ...props }) => {
  const { closeModal } = React.useContext(ModalContext);

  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    buttonRef.current?.focus();
  }, []);
  const callback = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      closeModal();
      if (onClick) {
        onClick(event);
      }
    },
    [closeModal, onClick]
  );
  return (
    <StyledModalDismissButton
      ref={buttonRef}
      noWrapper={props.noWrapper}
      className={className}
      onClick={callback}
      aria-label="Sulje"
      {...props}
    >
      {props.disabled ? <StyledModalDismissLoading /> : children}
    </StyledModalDismissButton>
  );
};

type ModalOpenButtonProps = Omit<ButtonProps, 'onClick'> & {
  className?: string;
  type?: buttonType;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  noStyle?: boolean;
  children?: React.ReactNode;
};

export const ModalOpenButton: React.FC<ModalOpenButtonProps> = ({
  type,
  onClick,
  noStyle,
  className,
  children,
  ...rest
}) => {
  const { openModal } = useContext(ModalContext);

  const callback = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      openModal();
      if (onClick) {
        onClick(event);
      }
    },
    [openModal, onClick]
  );

  if (noStyle) {
    return (
      <StealthButton {...rest} className={className} onClick={callback}>
        {children}
      </StealthButton>
    );
  }

  return (
    <Button
      {...rest}
      className={className}
      buttonTheme={type}
      onClick={callback}
      buttonHtmlType="button"
    >
      {children}
    </Button>
  );
};

type ModalProps = Omit<DialogProps, 'open'> & {
  closeButtonDisabled?: boolean;
  className?: string;
  noCloseButton?: boolean;
  noWrapper?: boolean;
  onModalClose?: () => void;
  unmounting?: boolean;
  headerImage?: string;
  ariaLabel?: string;
};

export const ModalContents: React.FC<ModalProps> = ({
  title,
  children,
  closeButtonDisabled,
  className,
  noCloseButton = false,
  noWrapper = false,
  headerImage,
  scroll,
  ariaLabel,
  unmounting, // eslint-disable-line @typescript-eslint/no-unused-vars
  ...props
}) => {
  const { isOpen, closeModal } = useContext(ModalContext);
  const isMobile = useMediaQuery({ query: mediaQuery.medium });
  const defaultScroll = isMobile ? 'paper' : 'body';

  return (
    <StyledDialog
      onClose={closeModal}
      scroll={scroll || defaultScroll}
      {...props}
      open={isOpen()}
      className={noWrapper ? className : ''}
      $isNativePlatform={getIsNativePlatform()}
      // modalStore already locks/unlocks scroll on <html> (see modalStore.ts), synchronously
      // with open/close. MUI's own scroll lock runs on <body> and, on close, is deferred until
      // the exit transition's onExited (closeAfterTransition on Dialog's internal Modal) — the
      // two locks releasing at different times produced a visible double shift on close.
      disableScrollLock
    >
      {noWrapper ? (
        <>
          {!noCloseButton && (
            <Container aria-label={ariaLabel}>
              <ModalDismissButton
                noWrapper
                data-testid="close-modal"
                disabled={closeButtonDisabled}
              >
                <ClearIcon style={{ fontSize: 32 }} htmlColor={headerImage ? 'white' : 'black'} />
              </ModalDismissButton>
            </Container>
          )}
          {children}
        </>
      ) : (
        <Container className={className} aria-label={ariaLabel}>
          <ModalHeader>
            {headerImage && <HeaderImage src={headerImage} alt="" aria-hidden="true" />}
            {!noCloseButton && (
              <ModalDismissButton data-testid="close-modal" disabled={closeButtonDisabled}>
                <StyledClearIcon htmlColor={headerImage ? 'white' : 'black'} fontSize="large" />
              </ModalDismissButton>
            )}
          </ModalHeader>
          <TextContent>
            {title && <Title as="h4">{title}</Title>}
            {children}
          </TextContent>
        </Container>
      )}
    </StyledDialog>
  );
};

export const StyledModalDismissButton = styled.button<{ noWrapper?: boolean, showOnLeftSide?: boolean }>`
  position: absolute;
  top: 10px;
  right: 10px;
  margin-top: 0;
  border: none;
  background: transparent;
  color: ${colors.gray60};
  cursor: pointer;
  padding: 0;
  border-radius: ${({ theme }) => theme.sizes?.borderRadius.default};
  transition: all 0.1s;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${colors.outlineDefault};
    outline-offset: 1px;
  }

  &:hover {
    color: ${colors.black};
  }

  ${(props) =>
    props.noWrapper &&
    css`
      color: ${colors.white};
      position: absolute;
      top: 10px;
      right: 10px;
      z-index: 1;

      &:hover {
        color: ${colors.white};
      }
    `}

  ${(props) =>
    props.showOnLeftSide &&
    css`
      right: auto;
      left: 10px;
      top: 20px;
    `}
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  h4 {
    font-weight: bold;
    margin: 0;
  }

  @media ${mediaQuery.medium} {
    margin-bottom: 8px;
  }
`;

const StyledModalDismissLoading = styled(LoadingIconCoupon)`
  margin-bottom: 12px;
`;

const StealthButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  transition: color 0.1s ease-in;

  &:focus {
    outline: none;
  }

  @media ${mediaQuery.medium} {
    padding: 0;
    width: 100%;
  }
`;

export const Title = styled(typography.HeadingMedium)`
  margin-bottom: 16px;
`;

export const StyledClearIcon = styled(ClearIcon)`
  font-size: 2em;
`;

export const HeaderImage = styled.img`
  width: 100%;
  height: 192px;
  padding: 24px 24px 0 24px;
  background: ${colors.green.base};
`;

export const StyledDialog = styled(Dialog)<{ $isNativePlatform: boolean }>`
  ${typography.textStyle}
  & .MuiDialog-container > .MuiPaper-root {
    padding-top: ${({ $isNativePlatform }) => ($isNativePlatform ? 'env(safe-area-inset-top)' : 0)};
  }

  .MuiDialog-paperWidthSm {
    max-width: 40rem !important;

    @media (min-width: 640px) {
      max-height: calc(100% - 64px) !important;
    }
  }

  @media ${mediaQuery.medium} {
    .MuiDialog-paperFullWidth,
    .MuiPaper-root {
      width: 100%;
      margin: 0;
      height: 100%;
      max-height: 100%;
      border-radius: 0;
    }
  }
`;

export const TextContent = styled.div`
  padding: 20px;
`;

export const ModalSeparator = styled.div`
  width: calc(100% + 38.4px);
  height: 1px;
  background-color: ${colors.borderDefault};
  margin: 0 -1.2rem;
`;

export const Container = styled.div`
  position: relative;
`;
